/**
 * DevSettingsDrawer - Side drawer for QA/Admin dev settings (TRAC-81)
 *
 * Provides testing controls like chunk timeout override for debugging
 * timeout scenarios without waiting 90 seconds.
 *
 * TRAC-291: Added experiment overrides section for testing experiment
 * variants without needing the correct session ID encoding.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getBackendApiUrl } from '../../config/backend';
import { useServedRegion } from '../../hooks/useServedRegion';
import { chaosParticipationService } from '../../services/chaosParticipationService';
import SegmentedControl from '../common/SegmentedControl';
import {
  HANDOFF_PROVIDER_LABELS,
  getHandoffProviderOverride,
  isHandoffProvider,
  isProdBuild,
  setHandoffProviderOverride,
  type HandoffProvider,
} from '../../services/handoffProviderOverride';
import {
  AGENT_MODEL_LABELS,
  getAgentModelOverride,
  isAgentModel,
  setAgentModelOverride,
  type AgentModel,
} from '../../services/agentModelOverride';
import {
  armFault,
  disarmFault,
  peekFault,
  FAULT_SHAPES,
  type FaultPlan,
  type FaultShape,
} from '../../services/streamFaultInjector';
import {
  readHours,
  setHours,
  hoursScopeStorageBlocked,
  type HoursOverride,
  type RegionHours,
} from '../../services/liveChatHoursOverride';
import styles from './DevSettingsDrawer.module.css';

const DEV_EXP_OVERRIDE_KEY = 'traci_dev_experiment_override';

interface FeatureInfo {
  name: string;
  variants: string[];
  description: string;
}

interface DevSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  chunkTimeout: number | null;
  onChunkTimeoutChange: (seconds: number | null) => void;
  onSimulateSessionExpiry?: () => Promise<void>;
  onSeverHandoffBridge?: (dropResume: boolean) => Promise<void>;
  handoffActive?: boolean;
  onExperimentChange?: () => void;
  getToken?: () => Promise<string | undefined>;
}

const DEFAULT_CHUNK_TIMEOUT = 90;

/** Parse the dev panel override from sessionStorage. Returns { LETTER: variantIndex } or empty. */
function getDevOverrides(): Record<string, number> {
  const stored = localStorage.getItem(DEV_EXP_OVERRIDE_KEY);
  if (!stored) return {};
  const assignments: Record<string, number> = {};
  const pattern = /([A-Za-z])(\d+)/g;
  for (const match of stored.matchAll(pattern)) {
    assignments[match[1].toUpperCase()] = parseInt(match[2], 10);
  }
  return assignments;
}

/** Encode overrides as "A1B2" format and write to sessionStorage. */
function setDevOverrides(overrides: Record<string, number>) {
  const entries = Object.entries(overrides).filter(([, v]) => v > 0).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    localStorage.removeItem(DEV_EXP_OVERRIDE_KEY);
  } else {
    localStorage.setItem(DEV_EXP_OVERRIDE_KEY, entries.map(([k, v]) => `${k}${v}`).join(''));
  }
}

export function DevSettingsDrawer({
  isOpen,
  onClose,
  chunkTimeout,
  onChunkTimeoutChange,
  onSimulateSessionExpiry,
  onSeverHandoffBridge,
  handoffActive,
  onExperimentChange,
  getToken,
}: DevSettingsDrawerProps) {
  const [inputValue, setInputValue] = useState<string>(
    chunkTimeout?.toString() ?? DEFAULT_CHUNK_TIMEOUT.toString()
  );
  const drawerRef = useRef<HTMLDivElement>(null);

  // TRAC-862: stream fault injection state.
  const [faultShape, setFaultShape] = useState<FaultShape>('heartbeat-only');
  const [faultCount, setFaultCount] = useState<number>(2);
  const [armed, setArmed] = useState<FaultPlan | null>(() => peekFault());

  // TRAC-291: Experiment overrides state
  const [features, setFeatures] = useState<Record<string, FeatureInfo>>({});
  const [expOverrides, setExpOverrides] = useState<Record<string, number>>(getDevOverrides);
  const [expLoading, setExpLoading] = useState(false);
  const [expError, setExpError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  // TRAC-450: region pin state — shared with the passive ExperimentBadge.
  const { region: servedRegion, pinned: regionPin, setPinned: setRegionPin } = useServedRegion();

  // TRAC-548: chaos drill participation toggle.
  const [chaosParticipating, setChaosParticipating] = useState<boolean>(
    () => chaosParticipationService.getParticipating(),
  );
  const [activeDrillId, setActiveDrillId] = useState<string | null>(
    () => chaosParticipationService.getDrillId(),
  );
  useEffect(() => {
    return chaosParticipationService.subscribe(({ participating, drillId }) => {
      setChaosParticipating(participating);
      setActiveDrillId(drillId);
    });
  }, []);
  const handleChaosToggle = (next: boolean) => {
    chaosParticipationService.setParticipating(next);
    setChaosParticipating(next);
  };

  // Agent version (v1 vs v2) — READ-ONLY indicator here so it's visible in
  // DEPLOYED envs (where the auth-off dev pill that shows AGENT: V1/V2 isn't
  // rendered). The flip is admin-gated (Cognito) and the chat widget is
  // partner-token-only (TRAC-486), so a write can't authenticate from here —
  // changing the version lives in Admin -> Settings -> System.
  const [agentVersion, setAgentVersion] = useState<string | null>(null);
  const [agentVersionEditable, setAgentVersionEditable] = useState<boolean>(false);
  const [agentVersionSaving, setAgentVersionSaving] = useState<boolean>(false);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getBackendApiUrl()}/api/settings/agent-version`);
        if (res.ok) {
          const d = await res.json();
          if (!cancelled && d?.version) setAgentVersion(String(d.version));
          if (!cancelled) setAgentVersionEditable(Boolean(d?.editable));
        }
      } catch { /* ignore — section just won't populate */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Flip v1<->v2 live from Dev Settings. Enabled only in non-prod (backend `editable`) AND only
  // when we can authenticate (getToken present = the Cognito-signed main app, not a partner
  // embed — the write is user-authed). Backend keeps prod admin-only. Takes effect on the next
  // message (~20s Config cache); no reload needed.
  const handleAgentVersionSelect = async (next: string) => {
    if (!getToken || next === agentVersion || agentVersionSaving) return;
    setAgentVersionSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${getBackendApiUrl()}/api/settings/agent-version`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: next }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d?.version) setAgentVersion(String(d.version));
      }
    } catch { /* ignore — control stays on the current version */ }
    finally { setAgentVersionSaving(false); }
  };

  // Live-chat hours override (TRAC-417): both regions, never in a prod build, hidden when the
  // backend says it is not available (404 in prod) or refuses this chat's token (TRACI-521:
  // a deployed env wants a tester; a shopper embed token gets 403).
  const [hours, setHoursState] = useState<RegionHours[]>([]);
  const [hoursBusy, setHoursBusy] = useState(false);
  const hoursSetRef = useRef(false); // a set started: a late initial read must not overwrite it
  useEffect(() => {
    if (!isOpen || isProdBuild()) return;
    let cancelled = false;
    readHours(getToken).then((r) => { if (!cancelled && !hoursSetRef.current) setHoursState(r); });
    return () => { cancelled = true; };
  }, [isOpen, getToken]);
  const hoursOk = hours.some((r) => !r.error);
  // TRACI-598: once a read worked, a failed set (429, a region down) keeps the switch and shows why.
  const [hoursWasUsable, setHoursWasUsable] = useState(false);
  if (hoursOk && !hoursWasUsable) setHoursWasUsable(true);
  const hoursUsable = !isProdBuild() && (hoursOk || hoursWasUsable);
  // A region that failed counts as disagreeing: never show a clean state for a half-applied flip.
  const hoursSeen = hours.map((r) => (r.error ? null : r.override));
  const hoursValue: HoursOverride | 'mixed' =
    hoursSeen.length > 0 && hoursSeen.every((o) => o === hoursSeen[0]) && hoursSeen[0] ? hoursSeen[0] : 'mixed';
  const hoursMinutes = Math.max(0, ...hours.map((r) => Math.ceil((r.expiresInSeconds ?? 0) / 60)));
  const hoursFailed = hours.filter((r) => r.error);
  // TRACI-555: the shared, whole-environment override (dev.sh or curl), which this browser follows when it has none of its own.
  const hoursShared = hours.find((r) => !r.error && r.shared && r.shared !== 'real')?.shared ?? null;
  const handleHoursSelect = async (next: string) => {
    if (hoursBusy || (next !== 'real' && next !== 'open' && next !== 'closed')) return;
    hoursSetRef.current = true;
    setHoursBusy(true);
    setHoursState(await setHours(next, getToken));
    setHoursBusy(false);
  };

  // TRAC-1188: where this browser's handoffs go (LivePerson or the Connect POC). The
  // global default comes from the public GET; the section shows only when the backend
  // says Connect is allowed here, and never in a prod build.
  const [handoffDefault, setHandoffDefault] = useState<HandoffProvider | null>(null);
  const [connectAllowed, setConnectAllowed] = useState<boolean>(false);
  const [handoffOverride, setHandoffOverrideState] = useState<HandoffProvider | null>(
    getHandoffProviderOverride,
  );
  useEffect(() => {
    if (!isOpen || isProdBuild()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getBackendApiUrl()}/api/settings/handoff-provider`);
        if (res.ok) {
          const d = await res.json();
          if (cancelled) return;
          setHandoffDefault(isHandoffProvider(d?.provider) ? d.provider : 'liveperson');
          setConnectAllowed(d?.connectAllowed === true);
        }
      } catch { /* ignore: section stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // TRACI-436: which Claude model this browser's chats use (dev and QA only; the
  // backend refuses the header on prod). Read on every request, so it applies to
  // the next message without a reload.
  const [agentModel, setAgentModelState] = useState<AgentModel | null>(getAgentModelOverride);
  const handleAgentModelSelect = (next: string) => {
    const value = isAgentModel(next) ? next : null;
    setAgentModelOverride(value);
    setAgentModelState(value);
  };

  const handleHandoffSelect = (next: string) => {
    const value = isHandoffProvider(next) ? next : null;
    setHandoffProviderOverride(value);
    setHandoffOverrideState(value);
  };

  // The reload preserves localStorage chat state, so the post-reload app
  // continues the same conversation against the newly-pinned region's
  // backend — turning the dev tool into a real continuity-test mechanism
  // (TRAC-548). The reload itself is still required so module-scoped
  // getBackendApiUrl() re-evaluates from localStorage.
  const handleRegionSelect = (next: 'auto' | 'us-east-1' | 'us-west-2') => {
    if (next === regionPin) return;
    const label = next === 'auto' ? 'auto-routing via Global Accelerator' : next;
    const confirmed = window.confirm(
      `Changing the region pin to ${label} reloads the page so already-loaded code picks up the new endpoint. Your conversation is preserved.`
    );
    if (!confirmed) return;
    setRegionPin(next);
  };

  // Sync input value when prop changes
  useEffect(() => {
    setInputValue(chunkTimeout?.toString() ?? DEFAULT_CHUNK_TIMEOUT.toString());
  }, [chunkTimeout]);

  // TRAC-291: Fetch experiment features when drawer opens
  const fetchFeatures = useCallback(async () => {
    if (!getToken || fetchedRef.current) return;
    fetchedRef.current = true;
    setExpLoading(true);
    setExpError(null);
    try {
      const token = await getToken();
      if (!token) {
        setExpError('Not authenticated');
        return;
      }
      const res = await fetch(`${getBackendApiUrl()}/api/experiments/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 403) {
          setExpError('Admin access required');
        } else {
          setExpError(`Failed to load (${res.status})`);
        }
        return;
      }
      const data = await res.json();
      const raw = data.features || {};
      const normalized: Record<string, FeatureInfo> = {};
      for (const [letter, feat] of Object.entries(raw) as [string, any][]) {
        normalized[letter.toUpperCase()] = {
          name: feat.name,
          variants: Array.isArray(feat.variants) ? feat.variants : feat.variant_names || [],
          description: feat.description || '',
        };
      }
      setFeatures(normalized);
    } catch {
      setExpError('Failed to connect');
    } finally {
      setExpLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (isOpen) {
      fetchFeatures();
    }
  }, [isOpen, fetchFeatures]);

  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Handle escape key to close
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const value = parseFloat(inputValue);
    if (!isNaN(value) && value >= 0.1 && value <= 90) {
      // Only set if different from default
      if (value === DEFAULT_CHUNK_TIMEOUT) {
        onChunkTimeoutChange(null);
      } else {
        onChunkTimeoutChange(value);
      }
    } else {
      // Reset to current value if invalid
      setInputValue(chunkTimeout?.toString() ?? DEFAULT_CHUNK_TIMEOUT.toString());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  const handleReset = () => {
    onChunkTimeoutChange(null);
    setInputValue(DEFAULT_CHUNK_TIMEOUT.toString());
  };

  // TRAC-291: Handle experiment variant selection
  // TRAC-478: Confirmation dialog removed — onExperimentChange routes through
  // handleClearSession which already gives a fresh welcome, so the "are you sure
  // you want to clear your chat?" prompt was redundant friction.
  const handleVariantSelect = (letter: string, variantIndex: number) => {
    const currentValue = expOverrides[letter] || 0;
    if (variantIndex === currentValue) return;

    const next = { ...expOverrides };
    if (variantIndex === 0) {
      delete next[letter];
    } else {
      next[letter] = variantIndex;
    }
    setExpOverrides(next);
    setDevOverrides(next);

    if (onExperimentChange) {
      onExperimentChange();
    }
  };

  const handleResetExperiments = () => {
    if (Object.keys(expOverrides).length === 0) return;

    setExpOverrides({});
    localStorage.removeItem(DEV_EXP_OVERRIDE_KEY);

    if (onExperimentChange) {
      onExperimentChange();
    }
  };

  if (!isOpen) return null;

  const isModified = chunkTimeout !== null && chunkTimeout !== DEFAULT_CHUNK_TIMEOUT;
  const hasExpOverrides = Object.keys(expOverrides).length > 0;
  const featureEntries = Object.entries(features).sort(([a], [b]) => a.localeCompare(b));

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} />

      {/* Drawer */}
      <div ref={drawerRef} className={styles.drawer}>
        <div className={styles.header}>
          <h3 className={styles.title}>Developer Settings</h3>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close dev settings"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.content}>
          {/* Agent Version. In NON-prod (backend `editable`) with a Cognito-authed session
              (getToken), it's a live v1<->v2 toggle so devs can switch in ephem/dev/qa without
              an admin role. On prod, or a partner embed that can't user-auth, it stays a
              read-only indicator (flip lives in Admin -> Settings -> System). */}
          <div className={styles.section}>
            <label className={styles.label}>
              Agent Version
              {agentVersion && <span className={styles.modifiedBadge}>{agentVersion.toUpperCase()}</span>}
            </label>
            {agentVersionEditable && getToken ? (
              <>
                <SegmentedControl
                  options={[
                    { value: 'v1', label: 'V1' },
                    { value: 'v2', label: 'V2' },
                  ]}
                  value={agentVersion ?? 'v1'}
                  onChange={(v) => handleAgentVersionSelect(v)}
                  ariaLabel="Agent version"
                />
                <p className={styles.hint}>
                  v1 = production agent; v2 = new capability agent. Switching takes effect on
                  your next message (~20s){agentVersionSaving ? ' — saving…' : ''}.
                </p>
              </>
            ) : (
              <p className={styles.hint}>
                v1 = production agent; v2 = new capability agent. Read-only here —
                change it in Admin → Settings → System (admin only).
              </p>
            )}
          </div>

          <div className={styles.divider} />

          {/* TRAC-417: live-chat hours override, so after-hours testing needs no script. Per browser since TRACI-555. */}
          {hoursUsable && (
            <>
              <div className={styles.section}>
                <label className={styles.label}>
                  Live chat hours
                  {hoursValue !== 'real' && <span className={styles.modifiedBadge}>{hoursValue === 'mixed' ? 'MIXED' : hoursValue.toUpperCase()}</span>}
                </label>
                <SegmentedControl
                  options={[
                    { value: 'real', label: 'Real hours' },
                    { value: 'open', label: 'Force open' },
                    { value: 'closed', label: 'Force closed' },
                  ]}
                  value={hoursValue === 'mixed' ? '' : hoursValue}
                  onChange={handleHoursSelect}
                  ariaLabel="Live chat hours"
                />
                <p className={styles.hint} data-testid="hours-status">
                  {hoursValue === 'real'
                    ? "Following LivePerson's real schedule."
                    : hoursValue === 'mixed'
                      ? 'Regions disagree; pick a state to set both.'
                      : `Forced ${hoursValue} in this browser only; clears itself in ${hoursMinutes} min.`}
                  {hoursValue === 'real' && hoursShared && ` Someone forced the whole environment ${hoursShared}.`}
                  {hoursBusy ? ' Saving…' : ''}
                  {hoursFailed.length > 0 && ` Failed: ${hoursFailed.map((r) => `${r.base} (${r.error})`).join(', ')}.`}
                </p>
                {/* TRACI-598: storage blocked (Safari, some third-party frames): the id lives only in this page. */}
                {hoursScopeStorageBlocked() && (
                  <p className={styles.hint} data-testid="hours-storage-blocked">
                    This browser blocks storage for the chat, so this setting lasts only until the page reloads.
                  </p>
                )}
              </div>

              <div className={styles.divider} />
            </>
          )}

          {/* TRACI-436: per-browser agent model pick. */}
          {!isProdBuild() && (
            <>
              <div className={styles.section} data-testid="agent-model-section">
                <label className={styles.label}>
                  Agent model
                </label>
                <SegmentedControl
                  options={[
                    { value: 'default', label: 'Default' },
                    { value: 'sonnet-4-6', label: AGENT_MODEL_LABELS['sonnet-4-6'] },
                    { value: 'sonnet-5-5', label: AGENT_MODEL_LABELS['sonnet-5-5'] },
                    { value: 'haiku-5-5', label: AGENT_MODEL_LABELS['haiku-5-5'] },
                  ]}
                  value={agentModel ?? 'default'}
                  onChange={handleAgentModelSelect}
                  ariaLabel="Agent model"
                />
                <p className={styles.hint}>
                  {agentModel
                    ? `Your next messages run on ${AGENT_MODEL_LABELS[agentModel]}.`
                    : 'Your messages run on this environment\'s default model.'}
                  {' '}Only this browser; nobody else's chats change.
                </p>
              </div>

              <div className={styles.divider} />
            </>
          )}

          {/* TRAC-1188: per-browser handoff provider override. */}
          {connectAllowed && !isProdBuild() && (
            <>
              <div className={styles.section}>
                <label className={styles.label}>
                  Human handoff
                </label>
                <div className={styles.expInfoBox}>
                  Global default: <strong>{HANDOFF_PROVIDER_LABELS[handoffDefault ?? 'liveperson']}</strong>
                </div>
                <SegmentedControl
                  options={[
                    { value: 'default', label: 'Use default' },
                    { value: 'liveperson', label: HANDOFF_PROVIDER_LABELS.liveperson },
                    { value: 'connect', label: HANDOFF_PROVIDER_LABELS.connect },
                  ]}
                  value={handoffOverride ?? 'default'}
                  onChange={handleHandoffSelect}
                  ariaLabel="Human handoff"
                />
                <p className={styles.hint}>
                  {handoffOverride
                    ? `This browser's live chats go to ${HANDOFF_PROVIDER_LABELS[handoffOverride]}.`
                    : `Use global default (currently ${HANDOFF_PROVIDER_LABELS[handoffDefault ?? 'liveperson']}).`}
                  {' '}Only this browser; applies to your next live chat request.
                </p>
              </div>

              <div className={styles.divider} />
            </>
          )}

          {/* TRAC-291: Experiment Overrides */}
          <div className={styles.section}>
            <label className={styles.label}>
              Experiment Overrides
              {hasExpOverrides && <span className={styles.modifiedBadge}>Modified</span>}
            </label>

            {expLoading && (
              <p className={styles.hint}>Loading experiments...</p>
            )}

            {expError && (
              <div className={styles.expManualOverride}>
                <p className={styles.hint}>{expError}</p>
                <p className={styles.hint}>Enter override code manually (e.g., A2):</p>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    placeholder="A2"
                    className={styles.input}
                    defaultValue={localStorage.getItem('traci_dev_experiment_override') || ''}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = (e.target as HTMLInputElement).value.trim().toUpperCase();
                        const confirmed = window.confirm(
                          'Changing experiments starts a new conversation. Current chat will be cleared. Continue?'
                        );
                        if (!confirmed) return;
                        if (val) {
                          localStorage.setItem('traci_dev_experiment_override', val);
                          setExpOverrides(getDevOverrides());
                        } else {
                          localStorage.removeItem('traci_dev_experiment_override');
                          setExpOverrides({});
                        }
                        if (onExperimentChange) {
                          onExperimentChange();
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {!expLoading && !expError && featureEntries.length === 0 && (
              <div className={styles.expInfoBox}>
                No experiments configured — check agent deployment
              </div>
            )}

            {!expLoading && !expError && featureEntries.map(([letter, feature]) => (
              <div key={letter} className={styles.expFeature}>
                <div className={styles.expFeatureHeader}>
                  <span className={styles.expFeatureLetter}>{letter}</span>
                  <span className={styles.expFeatureName}>{feature.name}</span>
                </div>
                <SegmentedControl
                  options={[
                    { value: '0', label: 'Default' },
                    ...feature.variants.map((variantName, idx) => ({
                      value: String(idx + 1),
                      label: variantName,
                    })),
                  ]}
                  value={String(expOverrides[letter] ?? 0)}
                  onChange={(v) => handleVariantSelect(letter, Number(v))}
                  ariaLabel={feature.name}
                />
              </div>
            ))}

            {hasExpOverrides && (
              <button
                className={styles.expResetButton}
                onClick={handleResetExperiments}
              >
                Clear All Overrides
              </button>
            )}
          </div>

          <div className={styles.divider} />

          {/* TRAC-450: Region Pin — lets devs/QA force the frontend to hit a
              specific region's ALB directly (api-{env}-useast1 or
              api-{env}-uswest2) instead of Global Accelerator. Reloads on
              change so the module-scoped getBackendApiUrl() re-evaluates. */}
          <div className={styles.section}>
            <label className={styles.label}>
              Region Pin
              {regionPin !== 'auto' && <span className={styles.modifiedBadge}>Pinned</span>}
            </label>
            <div className={styles.expInfoBox}>
              Currently served by: <strong>{servedRegion ?? 'detecting…'}</strong>
            </div>
            <SegmentedControl
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'us-east-1', label: 'us-east-1' },
                { value: 'us-west-2', label: 'us-west-2' },
              ]}
              value={regionPin}
              onChange={(v) => handleRegionSelect(v as 'auto' | 'us-east-1' | 'us-west-2')}
              ariaLabel="Region pin"
            />
          </div>

          <div className={styles.divider} />

          {/* TRAC-548: Chaos drill participation. When ON and a drill is
              active (active_chaos_drill_id surfaced via the announcement
              poll), useRegionHealth attaches the X-Chaos-Drill header to
              its region-pinned probes. Backend's chaos middleware 503s
              those probes, letting us observe pin-flip detection in real
              time without taking down the network. */}
          <div className={styles.section}>
            <label className={styles.label}>
              Chaos drill participation
              {chaosParticipating && <span className={styles.modifiedBadge}>Participating</span>}
            </label>
            <div className={styles.expInfoBox}>
              Active drill: <strong>{activeDrillId ?? 'none'}</strong>
              {chaosParticipating && !activeDrillId && (
                <div className={styles.hint}>
                  Toggle is ON but no drill is active. Soft 503s will only fire when the operator activates a drill.
                </div>
              )}
            </div>
            <SegmentedControl
              options={[
                { value: 'off', label: 'Off' },
                { value: 'on', label: 'On (participate)' },
              ]}
              value={chaosParticipating ? 'on' : 'off'}
              onChange={(v) => handleChaosToggle(v === 'on')}
              ariaLabel="Chaos drill participation"
            />
          </div>

          <div className={styles.divider} />

          {/* Chunk Timeout */}
          <div className={styles.section}>
            <label className={styles.label} htmlFor="chunkTimeout">
              Chunk Timeout
              {isModified && <span className={styles.modifiedBadge}>Modified</span>}
            </label>
            <div className={styles.inputGroup}>
              <input
                id="chunkTimeout"
                type="number"
                min="0.1"
                max="90"
                step="0.1"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                className={styles.input}
              />
              <span className={styles.inputSuffix}>seconds</span>
            </div>
            <p className={styles.hint}>
              Override the backend chunk read timeout (0.1-90s).
              <br />
              Default: {DEFAULT_CHUNK_TIMEOUT}s. Set low to test timeout handling.
            </p>
          </div>

          <div className={styles.divider} />

          {onSimulateSessionExpiry && (
            <button
              className={styles.simulateButton}
              onClick={async () => {
                toast('Armed — next message will trigger session expiry', { icon: '⏱️' });
                await onSimulateSessionExpiry();
              }}
            >
              Simulate Session Expiry
            </button>
          )}

          <div className={styles.divider} />

          {/* TRAC-862: break the next N turns on purpose, in a chosen shape.
              Before this, the only way to produce a broken stream was killing the
              agent — one shape, non-deterministic, and unable to reproduce the
              crash-after-content case that caused the incident. "Turns to fail"
              is the useful knob: 2 lets you watch a retry actually recover, 99
              drives the chain into the emergency overlay. */}
          <div className={styles.section}>
            <label className={styles.label}>Break the Stream (next N turns)</label>
            <div className={styles.inputGroup}>
              <select
                className={styles.input}
                value={faultShape}
                onChange={(e) => setFaultShape(e.target.value as FaultShape)}
              >
                {FAULT_SHAPES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <input
                className={styles.input}
                type="number"
                min={1}
                max={99}
                style={{ maxWidth: 70 }}
                value={faultCount}
                onChange={(e) => setFaultCount(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div className={`${styles.inputGroup} ${styles.inputGroupStacked}`}>
              <button
                className={styles.simulateButton}
                onClick={() => {
                  armFault(faultShape, faultCount);
                  setArmed(peekFault());
                  toast(`Next ${faultCount} turn(s) will fail: ${faultShape}`, { icon: '💥' });
                }}
              >
                Arm
              </button>
              <button
                className={styles.simulateButton}
                disabled={!armed}
                onClick={() => {
                  disarmFault();
                  setArmed(null);
                  toast('Fault injection disarmed', { icon: '✅' });
                }}
              >
                Disarm
              </button>
            </div>
            <p className={styles.hint}>
              {armed
                ? `ARMED: ${armed.shape} — ${armed.remaining} turn(s) left. These turns never reach the backend; a script feeds the real reader so the watchdog, retries and overlay all run for real.`
                : FAULT_SHAPES.find((s) => s.value === faultShape)?.blurb}
            </p>
          </div>

          <div className={styles.divider} />

          {/* TRAC-732: reproduce a redeploy mid-handoff. The bridge to LivePerson lives in
              memory, so a deploy severs it while the backend still thinks you're with a rep.
              Only reachable during a live chat — there's no bridge to sever otherwise. */}
          {onSeverHandoffBridge && (
            <div className={styles.section}>
              <label className={styles.label}>Live Chat Bridge</label>
              <div className={styles.inputGroup}>
                <button
                  className={styles.simulateButton}
                  disabled={!handoffActive}
                  onClick={async () => {
                    try {
                      await onSeverHandoffBridge(false);
                      toast('Bridge severed — should reconnect on the next poll', { icon: '🔌' });
                    } catch {
                      toast.error('Sever failed — check the backend logs');
                    }
                  }}
                >
                  Sever (recoverable)
                </button>
                <button
                  className={styles.simulateButton}
                  disabled={!handoffActive}
                  onClick={async () => {
                    try {
                      await onSeverHandoffBridge(true);
                      toast('Bridge severed + resume dropped — chat should end', { icon: '💥' });
                    } catch {
                      toast.error('Sever failed — check the backend logs');
                    }
                  }}
                >
                  Sever (unrecoverable)
                </button>
              </div>
              <p className={styles.hint}>
                {handoffActive
                  ? 'Simulates a backend redeploy killing the LivePerson socket. Recoverable rejoins and replays anything the rep said meanwhile; unrecoverable also drops the resume record, so the chat should end into the survey.'
                  : 'Start a live chat with a human first — there is no bridge to sever yet.'}
              </p>
            </div>
          )}

          <div className={styles.divider} />

          <button
            className={styles.resetButton}
            onClick={handleReset}
            disabled={!isModified}
          >
            Reset to Defaults
          </button>

          <div className={styles.infoSection}>
            <p className={styles.infoText}>
              These settings are for testing only and affect API requests.
              Changes apply immediately to new messages.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default DevSettingsDrawer;
